/**
 * The URLs of this app that carry a credential, and how to take it out of a string.
 *
 * No imports: `@core/sentry/instrument` loads this before anything else.
 */

/**
 * Path prefixes whose next segment is a credential, one per app route or API endpoint that takes one. A prefix
 * matches anywhere in a string, in any case and with its slashes percent-encoded or not, so it covers the page URL,
 * the route, an API endpoint that shares it and a return_url that carries it.
 */
export const TOKEN_PATH_PREFIXES = [
  '/auth/', // /auth/:token, the magic link
  '/guarantee-adjustment/', // /guarantee-adjustment/:token, the pay link, and /api/guarantee-adjustment/:token(/pay)
  '/reset/', // /reset/:token
  '/reset-password/check/', // /api/{lang}/reset-password/check/:token
  '/reservation-status/', // /reservation-status/:token
  '/unsubscribe/', // /unsubscribe/:token
  '/redeem-gift-card/', // /redeem-gift-card/:token
  '/get-gift-card/', // /api/{lang}/get-gift-card/:token
  '/confirm-reservation/', // /confirm-reservation/:id, an encrypted id that confirms the booking
  '/add-to-calendar/', // /add-to-calendar/:id, an encrypted id
  '/get-add-to-calendar-data/', // /api/get-add-to-calendar-data/:id
  '/confirm-booking/', // /api/confirm-booking/:token, which ConfirmEarlyAccessPage calls
];

/** The API endpoints under a prefix above whose next segment is their name, not a credential. */
const NAMED_SEGMENTS: Record<string, string[]> = {
  '/auth/': ['google', 'facebook', 'passwordless'], // /auth/google, /auth/facebook, /auth/passwordless/{send,verify}
};

/**
 * Query parameters that carry a credential wherever they appear: `code` is an OAuth callback's. The review link's `t`
 * is one only on that link (REVIEW_LINK_RE): the login return's `t` is a booking time.
 */
export const TOKEN_QUERY_PARAMS = ['token', 'authToken', 'code'];

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** A path separator, plain or percent-encoded. */
const SEPARATOR = '(?:/|%2F)';
/** A path separator after a segment, doubled or not. */
const SLASH = `${SEPARATOR}+`;
/**
 * A character of a credential: anything but a URL delimiter (plain or percent-encoded), whitespace, a quote, an angle
 * bracket or a backslash, so the rest of a JSON or HTML string survives.
 */
const PATH_VALUE_CHAR = `(?!%2F|%3F|%23|%26)[^/?#&\\s"'<>\\\\]`;
const QUERY_VALUE_CHAR = `(?!%26|%23)[^&#\\s"'<>\\\\]`;
/**
 * A prefix as a pattern, not followed by a segment its NAMED_SEGMENTS name. Its leading separator is a single one: a
 * repeated one would retry every slash of a run of them, quadratic in its length.
 */
function prefixPattern(prefix: string): string {
  const pattern = SEPARATOR + prefix.split('/').slice(1).map(escapeRegExp).join(SLASH);
  const names = NAMED_SEGMENTS[prefix];
  return names ? `${pattern}(?!(?:${names.map(escapeRegExp).join('|')})(?!${PATH_VALUE_CHAR}))` : pattern;
}

/** A segment that starts with `:` is a placeholder (`/auth/:token`, as normalizeEndpoint writes it), not a credential. */
const TOKEN_PATH_RE = new RegExp(
  `(${TOKEN_PATH_PREFIXES.map(prefixPattern).join('|')})(?!:)(?:${PATH_VALUE_CHAR})+`,
  'gi',
);
const queryParamRe = (names: string[]): RegExp => new RegExp(
  `((?:[?&#;]|%3F|%26|%23)(?:${names.map(escapeRegExp).join('|')})(?:=|%3D))(?:${QUERY_VALUE_CHAR})+`,
  'gi',
);
const TOKEN_QUERY_RE = queryParamRe(TOKEN_QUERY_PARAMS);
const PATH_SEGMENT = `(?:${PATH_VALUE_CHAR})+`;
/** The review link, `/review/:resId/:lang?…`, to the end of its query. */
const REVIEW_LINK_RE = new RegExp(
  `${SEPARATOR}review${SLASH}${PATH_SEGMENT}${SLASH}${PATH_SEGMENT}${SEPARATOR}*(?:\\?|%3F)(?:(?!%23)[^#\\s"'<>\\\\])*`,
  'gi',
);
const REVIEW_TOKEN_RE = queryParamRe(['t']);

/** `value` with every credential in it replaced by `replacement`, wherever and however often it appears. */
export function scrubUrl(value: string, replacement = '[REDACTED]'): string {
  const redactQueryValue = (_match: string, name: string) => `${name}${replacement}`;
  return value
    .replace(TOKEN_QUERY_RE, redactQueryValue)
    .replace(REVIEW_LINK_RE, (link) => link.replace(REVIEW_TOKEN_RE, redactQueryValue))
    .replace(TOKEN_PATH_RE, (_match, prefix: string) => `${prefix}${replacement}`);
}

/** Whether `url` carries a credential. */
export function carriesCredential(url: string): boolean {
  return scrubUrl(url) !== url;
}
